import { useGetDownloadSignedUrlForTaskQuestionLazyQuery } from '@exam-management/graphql';
import { extractImagesFileNames, updateImageTags } from '@exam-management/rich-text-editor';
import { useCallback, useEffect, useState } from 'react';

export interface UseParseContentForSingleQuestionInput {
  questionId: string;
  taskId: string;
}

export interface UseParseContentForItemSetQuestionInput {
  questionGroupId: string;
  taskId: string;
}

type UseParseContentForQuestionCreationInput =
  | UseParseContentForSingleQuestionInput
  | UseParseContentForItemSetQuestionInput;

export function useParseContentForQuestionReview(params: UseParseContentForQuestionCreationInput) {
  const questionGroupId = 'questionGroupId' in params ? params.questionGroupId : undefined;
  const questionId = 'questionId' in params ? params.questionId : undefined;
  const taskId = params.taskId;

  const [getDownloadSignedUrlForQuestion] = useGetDownloadSignedUrlForTaskQuestionLazyQuery();

  const parseContentForDisplay = useCallback(
    async (content: string) => {
      const fileNames = extractImagesFileNames(content);

      if (fileNames.length === 0) {
        return content;
      }

      const { data } = await getDownloadSignedUrlForQuestion({
        variables: {
          input: { fileNames, questionId, questionGroupId },
          taskId,
        },
      });

      const urlObject: Record<string, string> | undefined =
        data?.qctGetDownloadSignedUrlForTaskQuestion?.signedUrls;

      if (!urlObject) {
        return content;
      }

      return updateImageTags(content, urlObject);
    },
    [taskId, questionId, questionGroupId, getDownloadSignedUrlForQuestion],
  );

  return { parseContentForDisplay };
}

type UseParseQuestionContentInput =
  | {
      content: string;
      taskId: string;
      questionId: string;
    }
  | {
      content: string;
      taskId: string;
      questionGroupId: string;
    };

export function useParseQuestionContent(params: UseParseQuestionContentInput) {
  const { content, ...restParams } = params;

  const { parseContentForDisplay } = useParseContentForQuestionReview(restParams);
  const [parsedContent, setParsedContent] = useState('');

  const handleParsedImage = useCallback(async () => {
    if (!content) return;

    const parsedContent = await parseContentForDisplay(content);

    setParsedContent(parsedContent);
  }, [content, parseContentForDisplay]);

  useEffect(() => {
    handleParsedImage();
  }, [handleParsedImage]);

  return {
    content: parsedContent,
  };
}
